import { CheckboxOption } from '@types';
import clsx from 'clsx';
import React, { InputHTMLAttributes, useEffect, useState } from 'react';

import { Icon } from '../Icon/Icon';
import { Input } from '../Input/Input';

import styles from './Checkbox.module.scss';

export interface CheckboxGroupProps {
  options: CheckboxOption[];
  defaultSelected: string[];
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  onSelect: (keys: string[], option: string) => void;
  style?: React.CSSProperties;
  className?: string;
  disabledSearchList?: boolean;
  readOnly?: boolean;
}

interface CheckboxProps {
  option?: CheckboxOption;
  isChecked: boolean;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  toggleOption: (option: CheckboxOption) => void;
  className?: string;
  checkboxClassName?: string;
  testId?: string;
}

interface CheckProps {
  isChecked: boolean;
  indeterminate?: boolean;
  className?: string;
  testId?: string;
}

export const Check: React.FC<CheckProps> = ({ isChecked, indeterminate, className, testId }) => (
  <div
    className={clsx(styles.check, className)}
    data-testid={testId}
  >
    {isChecked && (
      <Icon
        className={styles.checkIcon}
        name="checkmark"
      />
    )}
    {indeterminate && (
      <Icon
        className={styles.checkIcon}
        name="dash"
      />
    )}
  </div>
);
export const Checkbox: React.FC<CheckboxProps> = React.forwardRef(
  ({ option, className, isChecked, inputProps, toggleOption, checkboxClassName, testId }, ref) => (
    <label
      className={clsx(styles.wrapper, isChecked && styles.checked, checkboxClassName)}
      data-testid={testId}
    >
      <Check
        className={className}
        isChecked={isChecked}
        testId={option?.id ? `checkbox-${option.id}` : undefined}
      />
      <input
        {...inputProps}
        type="checkbox"
        name={option.id.toString()}
        className={styles.input}
        checked={isChecked}
        onChange={() => toggleOption(option)}
      />
      <div className={styles.labelWrapper}>
        <p className={styles.label}>{option.name}</p>
        {option.icon && <div className={styles.icon}>{option.icon}</div>}
      </div>
    </label>
  ),
);

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  options,
  style,
  className,
  defaultSelected,
  inputProps,
  disabledSearchList = false,
  readOnly,
  onSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedValue, setSelectedValue] = useState(defaultSelected); // local checkbox group selected values

  useEffect(() => {
    setSelectedValue(defaultSelected);
  }, [defaultSelected]);

  function toggleOption(option: CheckboxOption) {
    let filteredOptions = [];
    if (selectedValue.includes(option.id.toString())) {
      filteredOptions = selectedValue.filter(selectedKey => selectedKey !== option.id.toString());
    } else {
      filteredOptions = [...selectedValue, option.id];
    }
    setSelectedValue(filteredOptions);
    onSelect(filteredOptions, option.id.toString());
  }

  return (
    <div
      style={style}
      className={clsx(styles.group, className)}
    >
      {options.length > 6 && !disabledSearchList && (
        <Input
          className={styles.searchInput}
          props={{ placeholder: 'Search list', onChange: e => setSearchTerm(e.target.value) }}
        />
      )}
      <div className={clsx(styles.list, { [styles.readOnly]: readOnly })}>
        {options
          .filter(
            option =>
              searchTerm === '' ||
              option.name
                .toString()
                .toLowerCase()
                .split(' ')
                .some(optionName => optionName.startsWith(searchTerm.toLowerCase())) ||
              option.name.toString().toLowerCase().startsWith(searchTerm.toLowerCase()),
          )
          .map(option => (
            <Checkbox
              {...inputProps}
              key={option.id}
              option={option}
              isChecked={selectedValue.includes(option.id.toString())}
              toggleOption={toggleOption}
            />
          ))}
      </div>
    </div>
  );
};
